import type { ReactNode } from "react";

/**
 * A titled section of a page's main column.
 *
 * Flat rather than carded: the page is one document read top to bottom, and
 * boxing each section would set every one at the same weight. Hierarchy
 * comes from the heading and the space around it instead — which is what
 * lets a long page stay scannable without a tab bar.
 */
export function BodySection({
  title,
  description,
  action,
  footer,
  children,
}: {
  title: string;
  /** One line under the heading, when the section's purpose is not obvious. */
  description?: string;
  action?: ReactNode;
  /**
   * Rendered under the content, left-aligned. "View all" lives here rather
   * than beside the heading: it is what you do after reading the preview,
   * not a property of the section.
   */
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-foreground text-xl font-semibold">{title}</h2>
          {description && (
            <p className="text-muted-foreground mt-1 text-sm">{description}</p>
          )}
        </div>
        {action}
      </div>
      {children}
      {footer && <div className="mt-3">{footer}</div>}
    </section>
  );
}
